import { createApi } from "@reduxjs/toolkit/query/react";
import { ApiEndpoints, baseQueryWithAuth } from "../../core";
import {
  ApiResponse,
  ITax,
  CreateTaxRequest,
  EditTaxRequest,
} from "../../types";
import { toast } from "../../core/toast";

export const taxApi = createApi({
  reducerPath: "taxApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Tax"],

  endpoints: (builder) => ({
    // ---------- GET ALL TAX ----------
    getTaxes: builder.query<ITax[], { search?: string; type?: string } | void>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params) {
          if (params.search) queryParams.append("search", params.search);
          if (params.type) queryParams.append("type", params.type);
        }
        return {
          url: `${ApiEndpoints.getAllTaxes}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`,
          method: "GET",
        };
      },
      transformResponse: (res: ApiResponse<ITax[]>) => res.results || [],
      providesTags: ["Tax"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error fetching taxes");
        }
      },
    }),

    // ---------- GET TAX BY ID ----------
    getTaxById: builder.query<ITax, string>({
      query: (id) => ({
        url: `${ApiEndpoints.getTaxById}/${id}`,
        method: "GET",
      }),
      transformResponse: (res: ApiResponse<ITax>) => res.result!,
      providesTags: (result, error, id) => [{ type: "Tax", id }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error fetching tax detail");
        }
      },
    }),

    // ---------- CREATE TAX ----------
    createTax: builder.mutation<ApiResponse<ITax>, CreateTaxRequest>({
      query: (body) => ({
        url: ApiEndpoints.createTax,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Tax"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || "Tax created successfully");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error creating tax");
        }
      },
    }),

    // ---------- EDIT TAX ----------
    editTax: builder.mutation<ApiResponse<ITax>, EditTaxRequest>({
      query: ({ id, data }) => ({
        url: `${ApiEndpoints.editTax}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => ["Tax", { type: "Tax", id }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || "Tax updated successfully");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error updating tax");
        }
      },
    }),

    // ---------- DELETE TAX ----------
    deleteTax: builder.mutation<ApiResponse<{ ok: boolean }>, string>({
      query: (id) => ({
        url: `${ApiEndpoints.deleteTax}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Tax"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || "Tax deleted successfully");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error deleting tax");
        }
      },
    }),
  }),
});

export const {
  useGetTaxesQuery,
  useGetTaxByIdQuery,
  useCreateTaxMutation,
  useEditTaxMutation,
  useDeleteTaxMutation,
} = taxApi;
